<script setup lang="ts">
import { ChevronRight, Database, Loader, Plug, Plus, SearchX } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { toast } from "vue-sonner";
import { testDatasource, updateDatasource, useDatasources, useDatasourceTypes } from "@/api/admin";
import type { Datasource } from "@/api/contracts";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Datasources", requires: { adminArea: "datasources" } } });

const queryClient = useQueryClient();
const datasources = useDatasources();
const types = useDatasourceTypes();
const q = useRouteQuery("q");
const env = useRouteQuery("env");
const testing = ref<string | null>(null);
const results = ref<Record<string, { ok: boolean; message: string; latencyMs?: number }>>({});

const typeLabel = (type: string) => types.data.value?.find((x) => x.type === type)?.label ?? type;
const typeTestable = (type: string) => types.data.value?.find((x) => x.type === type)?.testable ?? false;

const filtered = computed(() => {
  const needle = q.value.trim().toLowerCase();
  return (datasources.data.value ?? []).filter(
    (d) =>
      (!env.value || d.labels.environment === env.value) &&
      (!needle || [d.name, d.type, d.description, d.target ?? "", ...Object.values(d.labels)].some((s) => String(s ?? "").toLowerCase().includes(needle))),
  );
});
const groups = computed(() => {
  const map = new Map<string, Datasource[]>();
  for (const d of filtered.value) {
    if (!map.has(d.type)) map.set(d.type, []);
    map.get(d.type)!.push(d);
  }
  return [...map.entries()].sort((a, b) => typeLabel(a[0]).localeCompare(typeLabel(b[0])));
});
const envOptions = ["prod", "staging", "qa", "dev", "shared"].map((e) => ({ value: e, label: e, count: (datasources.data.value ?? []).filter((d) => d.labels.environment === e).length }));
const envModel = computed({ get: () => env.value || undefined, set: (v: string | string[] | undefined) => (env.value = (v as string) ?? "") });

const key = (d: Datasource) => `${d.type}/${d.name}`;

async function test(d: Datasource) {
  testing.value = key(d);
  try {
    results.value = { ...results.value, [key(d)]: await testDatasource(d.type, d.name) };
  } catch (err) {
    results.value = { ...results.value, [key(d)]: { ok: false, message: errorMessage(err) } };
  } finally {
    testing.value = null;
  }
}

async function toggle(d: Datasource, enabled: boolean) {
  try {
    await updateDatasource(d.type, d.name, { enabled });
    await queryClient.invalidateQueries({ queryKey: ["admin", "datasources"] });
    toast.success(`${d.name} ${enabled ? "enabled" : "disabled"}.`);
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

const SENS_VARIANT: Record<string, "muted" | "info" | "warning" | "danger"> = { public: "muted", internal: "info", confidential: "warning", pii: "danger" };
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="t.admin.datasources" :description="t.admin.datasourcesDescription">
      <template #actions>
        <Button as-child><RouterLink to="/admin/datasources/new"><Plus /> {{ t.admin.newDatasource }}</RouterLink></Button>
      </template>
    </PageHeader>

    <FilterBar v-model:search="q" placeholder="Search name, type, lineage…" label="Filter datasources" class="mb-5">
      <template #chips><FilterChips v-model="envModel" :options="envOptions" label="Environment" /></template>
    </FilterBar>

    <ListSkeleton v-if="datasources.isPending.value" :rows="6" />
    <ErrorState v-else-if="datasources.error.value" :error="datasources.error.value" @retry="datasources.refetch()" />
    <EmptyState v-else-if="!datasources.data.value?.length" :icon="Database" title="No datasources yet" description="Add the databases, clusters and services Smake tools should reach.">
      <Button as-child><RouterLink to="/admin/datasources/new"><Plus /> {{ t.admin.newDatasource }}</RouterLink></Button>
    </EmptyState>
    <EmptyState v-else-if="groups.length === 0" :icon="SearchX" :title="t.common.noResults" />
    <div v-else class="flex flex-col gap-8">
      <section v-for="[type, list] in groups" :key="type">
        <h2 class="section-title mb-3">{{ typeLabel(type) }} <span class="font-normal text-muted-foreground">({{ list.length }})</span></h2>
        <ul class="flex flex-col gap-2">
          <li v-for="d in list" :key="key(d)" :class="cn('flex flex-col gap-2 rounded-lg border bg-card px-4 py-3', !d.enabled && 'opacity-70')">
            <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
              <RouterLink :to="`/admin/datasources/${d.type}/${d.name}`" class="group flex min-w-0 items-center gap-2">
                <span class="font-medium group-hover:text-primary">{{ d.name }}</span>
                <ChevronRight class="size-4 text-muted-foreground" aria-hidden="true" />
              </RouterLink>
              <template v-if="d.targets?.length">
                <Badge v-for="t in d.targets" :key="t" variant="accent" class="font-mono">{{ t }}</Badge>
              </template>
              <Badge v-else-if="d.target" variant="accent" class="font-mono">{{ d.target }}</Badge>
              <Badge v-else variant="muted" title="Without lineage and environment labels (or a target mapping in its config) this datasource serves no target; only grants without a lineage/environment restriction reach it.">no target</Badge>
              <Badge v-if="d.labels.sensitivity" :variant="SENS_VARIANT[d.labels.sensitivity] ?? 'muted'">{{ d.labels.sensitivity }}</Badge>
              <Badge v-if="d.labels.primary === 'true'" variant="muted">primary</Badge>
              <div class="ml-auto flex items-center gap-3">
                <Button v-if="typeTestable(d.type)" variant="outline" size="sm" :disabled="testing === key(d)" @click="test(d)">
                  <Loader v-if="testing === key(d)" class="animate-spin" /><Plug v-else /> {{ t.common.test }}
                </Button>
                <Switch :model-value="d.enabled" :aria-label="`${d.name} enabled`" @update:model-value="(v: boolean) => toggle(d, v)" />
              </div>
            </div>
            <p v-if="d.description" class="text-sm text-muted-foreground">{{ d.description }}</p>
            <p
              v-if="results[key(d)]"
              role="status"
              :class="cn('rounded-md px-2.5 py-1.5 text-xs', results[key(d)].ok ? 'bg-success-soft text-success' : 'bg-destructive-soft text-destructive')"
            >
              {{ results[key(d)].ok ? "Connected" : "Failed" }}: {{ results[key(d)].message }}<template v-if="results[key(d)].latencyMs !== undefined"> ({{ results[key(d)].latencyMs }} ms)</template>
            </p>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
